import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Dimensions,
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import Modal from 'react-native-modal';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { Button, TextInput } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import JobsContext from '../../context/JobsContext';
import { ResumeDocument, TemplateId } from '../../types';
import { Palette, radius, spacing, useThemePalette } from '../../theme';
import { track } from '../../lib/analytics';
import NativeAdCard from '../NativeAdCard';
import SectionNav, { SectionItem } from './SectionNav';
import PersonalInfoForm from './sections/PersonalInfoForm';
import ExperienceForm from './sections/ExperienceForm';
import EducationForm from './sections/EducationForm';
import SkillsForm from './sections/SkillsForm';
import LanguagesForm from './sections/LanguagesForm';
import LinksForm from './sections/LinksForm';
import TemplatePicker from './TemplatePicker';
import PersonalizationForm from './sections/PersonalizationForm';
import ResumePreview from './ResumePreview';
import ExportSheet from './ExportSheet';

type SectionKey =
  | 'personalInfo'
  | 'experience'
  | 'education'
  | 'skills'
  | 'languages'
  | 'links'
  | 'template'
  | 'personalization'
  | 'preview';

interface Props {
  visible: boolean;
  initial: ResumeDocument;
  onDismiss: () => void;
}

const ResumeEditor: React.FC<Props> = ({ visible, initial, onDismiss }) => {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);
  const { updateResume } = useContext(JobsContext);

  const [draft, setDraft] = useState<ResumeDocument>(initial);
  const [active, setActive] = useState<SectionKey>('personalInfo');
  const [showExport, setShowExport] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Reset local draft when the editor is reopened with a different resume.
  // The visibility flip is the trigger; intentionally not depending on `initial`
  // because the parent recreates the object reference each render.
  useEffect(() => {
    if (visible) {
      setDraft(initial);
      setActive('personalInfo');
      setDirty(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, initial.id]);

  const patch = (next: Partial<ResumeDocument>) => {
    setDraft((d) => ({ ...d, ...next }));
    setDirty(true);
  };

  const sections: SectionItem[] = [
    { id: 'personalInfo', label: t('resume.sections.personalInfo') },
    { id: 'experience', label: t('resume.sections.experience') },
    { id: 'education', label: t('resume.sections.education') },
    { id: 'skills', label: t('resume.sections.skills') },
    { id: 'languages', label: t('resume.sections.languages') },
    { id: 'links', label: t('resume.sections.links') },
    { id: 'template', label: t('resume.sections.template') },
    { id: 'personalization', label: t('resume.sections.personalization') },
    { id: 'preview', label: t('resume.sections.preview') },
  ];

  const activeIndex = sections.findIndex((s) => s.id === active);
  const canGoPrev = activeIndex > 0;
  const canGoNext = activeIndex < sections.length - 1;

  const goPrev = () => {
    if (canGoPrev) setActive(sections[activeIndex - 1].id as SectionKey);
  };
  const goNext = () => {
    if (canGoNext) setActive(sections[activeIndex + 1].id as SectionKey);
  };

  const save = () => {
    updateResume(draft);
    track('resume_edit', { id: draft.id, template: draft.template });
    setDirty(false);
  };

  const tryClose = () => {
    if (!dirty) {
      onDismiss();
      return;
    }
    Alert.alert(t('resume.discardTitle'), t('resume.discardMessage'), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('resume.buttons.save'),
        onPress: () => {
          save();
          onDismiss();
        },
      },
      {
        text: t('resume.buttons.discard'),
        style: 'destructive',
        onPress: onDismiss,
      },
    ]);
  };

  const onExportPress = () => {
    if (dirty) save();
    setShowExport(true);
  };

  const onTemplateChange = (template: TemplateId) => {
    patch({ template });
    track('resume_template_change', { template });
  };

  // Fire one preview event per "session" of opening that chip. A new event
  // fires when the user navigates away and back.
  useEffect(() => {
    if (active === 'preview') {
      track('resume_preview', { template: draft.template });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const { height: deviceHeight } = Dimensions.get('window');

  return (
    <>
      <Modal
        isVisible={visible}
        onBackdropPress={tryClose}
        onBackButtonPress={tryClose}
        onSwipeComplete={tryClose}
        useNativeDriver
        useNativeDriverForBackdrop
        statusBarTranslucent
        // Keyboard handling lives inside KeyboardAwareScrollView below, so
        // we don't ask the modal itself to translate. avoidKeyboard would
        // double-shift content and crop the bottom of the editor.
        avoidKeyboard={false}
        propagateSwipe
        style={styles.modal}
        deviceHeight={deviceHeight}
        backdropOpacity={0.5}
      >
        <SafeAreaView style={styles.card} edges={['top', 'bottom']}>
          <View style={styles.header}>
            <TextInput
              mode="flat"
              value={draft.name}
              onChangeText={(v) => patch({ name: v })}
              style={styles.name}
              underlineColor="transparent"
              activeUnderlineColor={palette.primary}
              placeholder={t('resume.namePlaceholder')}
            />
            <TouchableOpacity
              onPress={tryClose}
              hitSlop={8}
              accessibilityLabel={t('common.cancel')}
            >
              <MaterialCommunityIcons
                name="close"
                size={24}
                color={palette.textPrimary}
              />
            </TouchableOpacity>
          </View>

          <SectionNav
            sections={sections}
            active={active}
            onChange={(id) => setActive(id as SectionKey)}
          />

          <KeyboardAwareScrollView
            style={styles.scroll}
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
            enableOnAndroid
            enableAutomaticScroll
            extraScrollHeight={80}
            extraHeight={120}
            keyboardOpeningTime={150}
          >
            {active === 'personalInfo' && (
              <PersonalInfoForm
                value={draft.personalInfo}
                onChange={(v) => patch({ personalInfo: v })}
              />
            )}
            {active === 'experience' && (
              <ExperienceForm
                value={draft.experience}
                onChange={(v) => patch({ experience: v })}
              />
            )}
            {active === 'education' && (
              <EducationForm
                value={draft.education}
                onChange={(v) => patch({ education: v })}
              />
            )}
            {active === 'skills' && (
              <SkillsForm value={draft.skills} onChange={(v) => patch({ skills: v })} />
            )}
            {active === 'languages' && (
              <LanguagesForm
                value={draft.languages}
                onChange={(v) => patch({ languages: v })}
              />
            )}
            {active === 'links' && (
              <LinksForm value={draft.links} onChange={(v) => patch({ links: v })} />
            )}
            {active === 'template' && (
              <TemplatePicker value={draft.template} onChange={onTemplateChange} />
            )}
            {active === 'personalization' && (
              <PersonalizationForm
                value={draft.personalization}
                onChange={(v) => patch({ personalization: v })}
              />
            )}
            {active === 'preview' && <ResumePreview resume={draft} />}

            <View style={styles.adSlot}>
              <NativeAdCard />
            </View>
          </KeyboardAwareScrollView>

          <View style={styles.navRow}>
            <Button
              mode="outlined"
              icon="chevron-left"
              onPress={goPrev}
              disabled={!canGoPrev}
              textColor={palette.textPrimary}
              style={styles.navBtn}
              compact
            >
              {t('resume.buttons.previous')}
            </Button>
            <Button
              mode="outlined"
              icon="chevron-right"
              onPress={goNext}
              disabled={!canGoNext}
              textColor={palette.textPrimary}
              style={styles.navBtn}
              contentStyle={styles.nextContent}
              compact
            >
              {t('resume.buttons.next')}
            </Button>
          </View>

          <View style={styles.footer}>
            <Button
              mode="outlined"
              icon="content-save-outline"
              onPress={save}
              disabled={!dirty}
              textColor={palette.textPrimary}
              style={styles.footerBtn}
            >
              {t('resume.buttons.save')}
            </Button>
            <Button
              mode="contained"
              icon="download"
              onPress={onExportPress}
              buttonColor={palette.primary}
              textColor="#0F1115"
              style={styles.footerBtn}
            >
              {t('resume.buttons.export')}
            </Button>
          </View>
        </SafeAreaView>
      </Modal>

      <ExportSheet
        visible={showExport}
        onDismiss={() => setShowExport(false)}
        resume={draft}
      />
    </>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    modal: { margin: 0, justifyContent: 'flex-end' },
    card: {
      backgroundColor: p.canvas,
      borderTopLeftRadius: radius.lg,
      borderTopRightRadius: radius.lg,
      flex: 1,
      marginTop: spacing.xl,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingLeft: spacing.md,
      paddingRight: spacing.lg,
      paddingTop: spacing.sm,
      gap: spacing.sm,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: p.border,
    },
    name: {
      flex: 1,
      backgroundColor: 'transparent',
      fontSize: 18,
      fontWeight: '700',
    },
    scroll: { flex: 1 },
    scrollContent: { paddingHorizontal: spacing.lg, paddingBottom: spacing.xxl },
    // NativeAdCard already brings its own horizontal padding via its
    // cardWrapper style, so the ad slot only needs vertical breathing room.
    adSlot: { marginTop: spacing.lg, marginHorizontal: -spacing.lg },
    navRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: spacing.sm,
      paddingHorizontal: spacing.md,
      paddingTop: spacing.sm,
      paddingBottom: spacing.lg,
    },
    navBtn: { flex: 1, borderRadius: radius.pill, borderColor: p.border },
    nextContent: { flexDirection: 'row-reverse' },
    footer: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: spacing.sm,
      padding: spacing.md,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: p.border,
      backgroundColor: p.surface,
    },
    footerBtn: { borderRadius: radius.pill },
  });

export default ResumeEditor;
